import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowPathIcon, ArrowRightIcon, HeartIcon } from '@heroicons/react/24/outline';

import { listHealthPlanImports } from '../services/healthPlanImportsService';
import type { HealthPlanImportDocument, HealthPlanProvider } from '../services/healthPlanTypes';
import type { FirestoreError } from 'firebase/firestore';
import { healthPlanProviders, healthPlanProviderBySlug } from './healthPlanProviders';
import { useToast } from '@/hooks/useToast';
import PaginationControls from '@/components/shared/PaginationControls/PaginationControls';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';

type ProviderFilter = HealthPlanProvider | 'all';

const getValidProvider = (value: string | null): HealthPlanProvider | '' => {
  if (!value) return '';
  return healthPlanProviderBySlug[value] ? (value as HealthPlanProvider) : '';
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export function HealthPlanImportsPage() {
  const { error: showError, success: showSuccess } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryProvider = getValidProvider(searchParams.get('provider'));

  const [providerFilter, setProviderFilter] = useState<ProviderFilter>(queryProvider || 'all');
  const [imports, setImports] = useState<HealthPlanImportDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    const next = queryProvider || 'all';
    setProviderFilter(next);
  }, [queryProvider]);

  useEffect(() => {
    let ignore = false;
    const fetchImports = async () => {
      setLoading(true);
      setPermissionDenied(false);
      setLoadError(false);
      try {
        if (providerFilter === 'all') {
          const responses = await Promise.allSettled(
            healthPlanProviders.map((provider) => listHealthPlanImports(provider.slug as HealthPlanProvider)),
          );
          if (ignore) return;
          const aggregated: HealthPlanImportDocument[] = [];
          let permissionIssue = false;
          let genericError = false;
          responses.forEach((result) => {
            if (result.status === 'fulfilled') {
              aggregated.push(...result.value);
            } else {
              const error = result.reason as FirestoreError;
              console.error('[HealthPlanImportsPage] failed to load provider', error);
              if (error?.code === 'permission-denied') {
                permissionIssue = true;
              } else {
                genericError = true;
              }
            }
          });
          aggregated.sort((a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0));
          setImports(aggregated);
          setPermissionDenied(permissionIssue);
          setLoadError(genericError);
          if (genericError) {
            showError('Não foi possível carregar todas as importações.');
          }
        } else {
          const data = await listHealthPlanImports(providerFilter);
          if (ignore) return;
          setImports(data);
        }
        setCurrentPage(1);
      } catch (err) {
        if (ignore) return;
        const error = err as FirestoreError;
        console.error('[HealthPlanImportsPage] failed to load', error);
        if (error.code === 'permission-denied') {
          setPermissionDenied(true);
        } else {
          showError('Não foi possível carregar as importações.');
          setLoadError(true);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    void fetchImports();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerFilter]);

  const totals = useMemo(() => {
    return imports.reduce(
      (acc, item) => {
        acc.premiumHealth += item.totals?.premiumHealth ?? 0;
        acc.copayTotal += item.totals?.copayTotal ?? 0;
        acc.totalCost += item.totals?.totalCost ?? (item.totals?.premiumHealth ?? 0) + (item.totals?.copayTotal ?? 0);
        acc.activeLives += item.totals?.activeLives ?? 0;
        return acc;
      },
      { premiumHealth: 0, copayTotal: 0, totalCost: 0, activeLives: 0 },
    );
  }, [imports]);

  const aggregatedExpenses = useMemo(() => {
    const groups = new Map<
      string,
      { competence: string; premiumHealth: number; copayTotal: number; totalCost: number; activeLives: number }
    >();
    imports.forEach((item) => {
      const competence = item.billingPeriod ?? 'Sem competência';
      const existing = groups.get(competence) ?? {
        competence,
        premiumHealth: 0,
        copayTotal: 0,
        totalCost: 0,
        activeLives: 0,
      };
      const premium = item.totals?.premiumHealth ?? 0;
      const copay = item.totals?.copayTotal ?? 0;
      const total = item.totals?.totalCost ?? premium + copay;
      existing.premiumHealth += premium;
      existing.copayTotal += copay;
      existing.totalCost += total;
      existing.activeLives += item.totals?.activeLives ?? 0;
      groups.set(competence, existing);
    });
    return Array.from(groups.values()).sort((a, b) => {
      if (a.competence === b.competence) return 0;
      if (!a.competence) return 1;
      if (!b.competence) return -1;
      return b.competence.localeCompare(a.competence);
    });
  }, [imports]);

  const totalPages = Math.max(1, Math.ceil(aggregatedExpenses.length / itemsPerPage));
  useEffect(() => {
    setCurrentPage(1);
  }, [aggregatedExpenses.length]);

  useEffect(() => {
    setCurrentPage((prev) => (prev > totalPages ? totalPages : prev));
  }, [totalPages]);

  const paginatedExpenses = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return aggregatedExpenses.slice(start, start + itemsPerPage);
  }, [aggregatedExpenses, currentPage, itemsPerPage]);

  const hasImports = imports.length > 0;

  const handleItemsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(event.target.value) || 10);
    setCurrentPage(1);
  };

  const goToPreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handleProviderChange = (value: ProviderFilter) => {
    setProviderFilter(value);
    const nextParams = new URLSearchParams(searchParams);
    if (value === 'all') {
      nextParams.delete('provider');
    } else {
      nextParams.set('provider', value);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const selectedProvider =
    providerFilter === 'all' ? null : healthPlanProviderBySlug[providerFilter] ?? null;

  const importHref =
    providerFilter === 'all'
      ? '/expenses/benefits/health-plan/import'
      : `/expenses/benefits/health-plan/import?provider=${providerFilter}`;

  return (
    <div className="space-y-6">
      <SubPageHeader
        backTo="/expenses/benefits"
        icon={<HeartIcon className="h-6 w-6" aria-hidden />}
        badge="Benefícios"
        title="Planos de saúde"
        description={
          selectedProvider
            ? `Visão das importações do convênio ${selectedProvider.name}.`
            : 'Visão consolidada das importações e totais por convênio.'
        }
        actions={
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
              <label htmlFor="provider-filter">Operadora</label>
              <select
                id="provider-filter"
                value={providerFilter}
                onChange={(event) => handleProviderChange((event.target.value as ProviderFilter) || 'all')}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:w-48"
              >
                <option value="all">Todos os planos</option>
                {healthPlanProviders.map((provider) => (
                  <option key={provider.slug} value={provider.slug}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </div>
            <Link
              to={importHref}
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-primary-700"
            >
              Importar
              <ArrowRightIcon className="h-4 w-4" aria-hidden />
            </Link>
          </div>
        }
      />

      {/* Importações recentes bloco removido conforme instrução */}

      <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Despesas</h2>
            <p className="text-sm text-gray-600">Histórico consolidado das importações com totais.</p>
          </div>
          {loading ? <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-500" aria-hidden /> : null}
        </div>
        {loading ? (
          <div className="mt-4 text-sm text-gray-500">Carregando importações...</div>
        ) : permissionDenied ? (
          <div className="mt-4 rounded-md border border-red-100 bg-red-50 p-4 text-sm text-red-700">
            Sem permissão para listar importações deste módulo. Ajuste as regras do Firestore ou use uma conta com acesso.
          </div>
        ) : loadError ? (
          <div className="mt-4 rounded-md border border-amber-100 bg-amber-50 p-4 text-sm text-amber-700">
            Não foi possível carregar as importações. Tente novamente mais tarde.
          </div>
        ) : !hasImports ? (
          <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            Nenhuma despesa registrada neste convênio.
          </div>
        ) : (
          <>
            <div className="mt-4 overflow-hidden rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Competência</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Prêmio saúde</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Copay</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Total</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Vidas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {paginatedExpenses.map((item) => (
                    <tr key={`expense-${item.competence}`}>
                      <td className="px-4 py-3 text-gray-900">
                        <Link
                          to={`/expenses/benefits/health-plan/imports/${encodeURIComponent(item.competence)}`}
                          className="text-primary-700 underline underline-offset-2 hover:text-primary-800"
                        >
                          {item.competence}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{formatCurrency(item.premiumHealth)}</td>
                      <td className="px-4 py-3 text-gray-700">{formatCurrency(item.copayTotal)}</td>
                      <td className="px-4 py-3 text-gray-700">{formatCurrency(item.totalCost)}</td>
                      <td className="px-4 py-3 text-gray-700">{item.activeLives || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={aggregatedExpenses.length}
              goToPreviousPage={goToPreviousPage}
              goToNextPage={goToNextPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </>
        )}
      </div>
    </div>
  );
}
